import type {
  AddCampaignRecipientsBody,
  AddRecipientsResult,
  BudgetAmendmentHistoryRow,
  CampaignContactsResponse,
  QuoteCampaignRecipientsBody,
  RecipientAdditionsQuote,
  UpdateCampaignContactBody,
} from "@/interfaces/campaignContacts";
import { apiClient } from "@/lib/apiClient";

/** GET /campaigns/:campaignId/contacts, returns the full envelope with contacts, spaces, and metadata. */
export async function fetchCampaignContacts(
  campaignId: string,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<CampaignContactsResponse> {
  const { data } = await apiClient.get<CampaignContactsResponse>(`${campaignPath}/contacts`);
  return data;
}

/** PATCH /campaigns/:campaignId/contacts/:contactId. Errors propagate to the caller. */
export async function updateCampaignContact(
  campaignId: string,
  contactId: number,
  body: UpdateCampaignContactBody,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<void> {
  await apiClient.patch(`${campaignPath}/contacts/${contactId}`, body);
}

/** POST /campaigns/:campaignId/contacts/:contactId/resend. Errors propagate to the caller. */
export async function resendCampaignContactEmail(
  campaignId: string,
  contactId: number,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<void> {
  await apiClient.post(`${campaignPath}/contacts/${contactId}/resend`);
}

/** DELETE /campaigns/:campaignId/contacts/:contactId. Errors propagate to the caller. */
export async function removeCampaignContact(
  campaignId: string,
  contactId: number,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<void> {
  await apiClient.delete(`${campaignPath}/contacts/${contactId}`);
}

/** POST /campaigns/:campaignId/contacts/additions/quote. Prices `body.count` new recipients (free spaces first, then paid); `body.depositMode` is the postpaid choice when the wallet is short. */
export async function quoteRecipientAdditions(
  campaignId: string,
  body: QuoteCampaignRecipientsBody,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<RecipientAdditionsQuote> {
  const { data } = await apiClient.post<{ success: true; data: RecipientAdditionsQuote }>(
    `${campaignPath}/contacts/additions/quote`,
    body,
  );
  return data.data;
}

/** POST /campaigns/:campaignId/contacts/additions. Adds recipients; `body.expected` echoes the quote being confirmed. */
export async function addCampaignRecipients(
  campaignId: string,
  body: AddCampaignRecipientsBody,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<AddRecipientsResult> {
  const { data } = await apiClient.post<{ success: true; data: AddRecipientsResult }>(
    `${campaignPath}/contacts/additions`,
    body,
  );
  return data.data;
}

/** GET /campaigns/:campaignId/budget-amendments. Oldest first. */
export async function fetchBudgetAmendments(
  campaignId: string,
  campaignPath = `/campaigns/${campaignId}`,
): Promise<BudgetAmendmentHistoryRow[]> {
  const { data } = await apiClient.get<{ success: true; data: BudgetAmendmentHistoryRow[] }>(
    `${campaignPath}/budget-amendments`,
  );
  return data.data;
}
